import { useState } from 'react'
import { Mail, X } from 'lucide-react'
import api from '@/services/api'
import { toast } from 'sonner'

interface ResetPasswordModalProps {
  userId: string
  onClose: () => void
  onDone: () => void
}

export function ResetPasswordModal({ userId, onClose, onDone }: ResetPasswordModalProps) {
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [sendingInvite, setSendingInvite] = useState(false)

  const handleReset = async () => {
    if (password.length < 6) {
      toast.error('Senha deve ter no mínimo 6 caracteres')
      return
    }
    setSaving(true)
    try {
      await api.post(`/users/${userId}/reset-password`, { password })
      toast.success('Senha redefinida com sucesso')
      onDone()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao redefinir senha'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const handleSendInvite = async () => {
    setSendingInvite(true)
    try {
      const res = await api.post(`/users/${userId}/send-password-setup`)
      const sent = Boolean(res.data?.data?.emailSent)
      toast.success(
        sent
          ? 'E-mail enviado. O usuário receberá um link para definir a senha.'
          : 'Solicitação registrada, mas o e-mail não pôde ser enviado.',
      )
      if (sent) onDone()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao enviar e-mail'
      toast.error(message)
    } finally {
      setSendingInvite(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-6 shadow-[0_24px_64px_rgba(0,0,0,0.55)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-[var(--text-primary)]">Redefinir Senha</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            <X size={16} />
          </button>
        </div>

        <button
          type="button"
          onClick={handleSendInvite}
          disabled={sendingInvite || saving}
          className="mb-4 flex w-full items-center justify-center gap-2 rounded-md border border-accent/30 bg-accent/10 px-4 py-2.5 text-xs font-bold text-accent transition-colors hover:bg-accent/15 disabled:opacity-50"
        >
          <Mail size={14} aria-hidden />
          {sendingInvite ? 'Enviando...' : 'Reenviar e-mail de cadastro'}
        </button>

        <p className="mb-3 text-center text-[11px] uppercase tracking-wider text-[var(--text-muted)]">
          ou defina manualmente
        </p>

        <div className="mb-4 flex flex-col gap-1.5">
          <label className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
            Nova Senha
          </label>
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mínimo 6 caracteres"
            type="password"
            className="min-h-[44px] w-full rounded-md border border-[var(--border)] bg-[var(--bg-main)] px-3 py-2 text-xs text-[var(--text-primary)] outline-none transition-colors placeholder:text-[var(--text-muted)] focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary-muted)]"
          />
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={saving || sendingInvite}
            className="rounded-md bg-accent px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-accent/90 disabled:opacity-50"
          >
            {saving ? 'Redefinindo...' : 'Salvar senha'}
          </button>
        </div>
      </div>
    </div>
  )
}
